/**
 * client/src/components/layout/nav-badge.tsx
 *
 * The attention count on a nav entry (#2171) — one component, rendered by BOTH
 * the desktop sidebar and the mobile bottom tabs, so the two can never drift.
 *
 * Visual treatment is the SAME interactive-toned "confirm" chip the comms
 * inbox already uses for unread counts (GroupConversationRow.tsx): an
 * action/attention signal, deliberately NEVER emerald — emerald is reserved
 * for success / hardware confirmed / AAL milestones (CLAUDE.md Design
 * Constraints) and a waiting invitation is none of those. Never amber or red
 * either: an unanswered invitation is not a user error and not a Forensic
 * Pause.
 *
 * Caps at "9+" like the notification bell, so a long-neglected inbox cannot
 * widen the chip past the icon it sits on.
 */

import { cn } from "@/lib/cn";

interface NavBadgeProps {
  count: number;
  /** What the count is about, for the accessible name (e.g. "Invitations"). */
  label: string;
  /** Bottom tabs pin the chip to the icon corner; the sidebar lays it inline. */
  className?: string;
}

export function NavBadge({ count, label, className }: NavBadgeProps): React.ReactElement | null {
  if (count <= 0) return null;
  return (
    <span
      data-testid="nav-badge"
      aria-label={`${count} pending ${label.toLowerCase()}`}
      className={cn(
        "min-w-[18px] text-center rounded-full px-1.5",
        "text-[length:var(--text-caption-size)] font-semibold leading-tight",
        "bg-[var(--challenge-confirm-bg)] text-[color:var(--challenge-confirm-fg)]",
        "border border-[var(--challenge-confirm-bd)]",
        className,
      )}
    >
      {count >= 10 ? "9+" : count}
    </span>
  );
}
